import { MeshBackground } from '@denqfabrik/landing-ui';
import { Link } from 'react-router-dom';
import PLATFORM_LOGOS from '../../data/platformLogos';
import { useTranslation } from '../../i18n';

/**
 * System names are proper nouns and language-invariant — same rule as
 * `data/integrations.ts`, which is why they are not in the i18n bundles.
 *
 * Both lists are drawn from the catalogue in `data/integrations.ts`, so the hero
 * cannot promise a system the configurator does not offer.
 */
const SOURCE_SYSTEMS = ['Shopware', 'WooCommerce', 'JTL', 'Plentymarkets'] as const;
const TARGET_SYSTEMS = ['Amazon', 'OTTO', 'Kaufland', 'Mirakl', 'DATEV'] as const;

/**
 * The chips carry brand marks, not names (René, iteration 3). Logos come from
 * `data/platformLogos.ts` — the app's existing name→asset map, already rendered by
 * `PlatformGrid` and `IntegrationSection` with this same grayscale+multiply recipe,
 * so there is no second logo map here.
 *
 * This table only records where the hero needs something else than the shared map,
 * measured in the browser at chip scale (22 px cap) on 2026-08-17:
 *   `null`   — no asset in `public/images/external_logos` survives 22 px, so the
 *              chip falls back to a text mark in the same box.
 *   a path   — the shared map's asset is unusable at 22 px but a sibling file works.
 * Everything not listed uses the shared map unchanged.
 */
const HERO_MARK_OVERRIDES: Record<string, string | null> = {
  // shared map points at a 400×400 JPEG of a small lockup — mush at 22 px.
  Kaufland: '/images/external_logos/marketplaces/Kaufland/Kaufland_idjLCxMgxt_6.svg',
  // Wide official wordmarks fetched 2026-08-18 (Amazon → Wikimedia, Mirakl →
  // mirakl.com). The shared-map assets were a smile-only Amazon and a 400×400
  // Mirakl bitmap, both mush at 22 px; these read cleanly grayscaled next to OTTO.
  Amazon: '/images/external_logos/marketplaces/Amazon/Amazon_wordmark.svg',
  Mirakl: '/images/external_logos/marketplaces/Mirakl/Mirakl_wordmark.svg',
  // DATEV's brand mark is a square colour block with knocked-out text and has no
  // wide wordmark — grayscale turns it into a grey box. Kept as a text chip, which
  // reads cleaner in the grey row than a lone colour box (René, 2026-08-18).
  DATEV: null,
};

const markFor = (name: string): string | null =>
  name in HERO_MARK_OVERRIDES ? HERO_MARK_OVERRIDES[name] : (PLATFORM_LOGOS[name] ?? null);

/** md+ height of the graph row — must stay in sync with `md:grid-rows-[auto_340px]`. */
const ROW_H = 340;
/** md+ chip height — must stay in sync with the `h-[42px]` in `Chip`. */
const CHIP_H = 42;

/**
 * Vertical centre of each chip, in % of the graph row, for `justify-between`
 * columns of equal-height chips: `c(i) = h/2 + i·(H−h)/(n−1)`.
 * The edge SVG is decorative, so a pixel of drift costs nothing — but keeping the
 * numbers derived rather than eyeballed means a 5th source lands in the right place.
 */
const rowCentres = (n: number): number[] => {
  const chip = (CHIP_H / ROW_H) * 100;
  return Array.from({ length: n }, (_, i) => chip / 2 + (i * (100 - chip)) / (n - 1));
};

const SOURCE_Y = rowCentres(SOURCE_SYSTEMS.length);
const TARGET_Y = rowCentres(TARGET_SYSTEMS.length);

/**
 * The hub's width as a % of the middle column, and therefore where the edges have to
 * start and stop. The edge SVG covers ONLY the middle column, which is what lets this be
 * a single fixed number regardless of how wide the card gets. A first attempt fixed all
 * three columns at 24/52/24 % of the card instead and clipped two chips at 1024 px.
 */
const HUB_PCT = 62;
const HUB_LEFT = (100 - HUB_PCT) / 2;
const HUB_RIGHT = 100 - HUB_LEFT;

/** One `d` per edge: sources into the hub, then the hub out to the targets. */
const EDGE_PATHS = [
  ...SOURCE_Y.map((y) => `M 0 ${y} C ${HUB_LEFT / 2} ${y}, ${HUB_LEFT / 2} 50, ${HUB_LEFT} 50`),
  ...TARGET_Y.map(
    (y) =>
      `M ${HUB_RIGHT} 50 C ${(HUB_RIGHT + 100) / 2} 50, ${(HUB_RIGHT + 100) / 2} ${y}, 100 ${y}`,
  ),
];

/**
 * One box for both kinds of mark, so a logo chip and a text chip are the same shape
 * and size — a row of mixed heights is what makes a half-logo column look unfinished.
 * `role="img"` + `aria-label` keeps the accessible name once the visible text is gone.
 */
function Chip({ label }: { label: string }) {
  const mark = markFor(label);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="flex h-[42px] w-full items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
    >
      {mark ? (
        <img
          src={mark}
          alt=""
          aria-hidden="true"
          className="max-h-[22px] w-auto max-w-full select-none object-contain opacity-90"
          style={{ filter: 'grayscale(100%)', mixBlendMode: 'multiply' }}
        />
      ) : (
        <span
          aria-hidden="true"
          className="truncate text-[0.74rem] font-semibold uppercase tracking-[0.06em] text-slate-700"
        >
          {label}
        </span>
      )}
    </span>
  );
}

function ColumnLabel({
  children,
  align,
  place,
}: {
  children: string;
  align: 'left' | 'center' | 'right';
  /** Explicit md+ placement — see the grid comment in IntegrationFlowGraph. */
  place: string;
}) {
  return (
    <p
      // Alignment is md+ only: stacked on a phone, all three captions share one left rag.
      className={`mb-3 text-[0.65rem] font-semibold uppercase tracking-[0.16em] ${place} ${
        align === 'center' ? 'text-sky-600 md:text-center' : 'text-slate-500'
      } ${align === 'right' ? 'md:text-right' : ''}`}
    >
      {children}
    </p>
  );
}

/**
 * The integration flow: your systems on the left, our sync in the middle, channels
 * and targets on the right. Inline SVG for the edges, CSS keyframes for the dash
 * flow — no animation library, no external asset (see the hero brief, 2026-08-17).
 *
 * A light surface card on the dark hero (iteration 2, René's live review): the same
 * contrast trick the old Case-Intake panel used — a light bezel frame around a
 * near-white surface, chips dark-on-white.
 *
 * The edges are `hidden md:block`: below `md` the three groups stack vertically, so
 * horizontal curves would be wrong AND would be the only thing able to overflow.
 */
function IntegrationFlowGraph() {
  const { t } = useTranslation();
  const f = t.hero.flow;

  return (
    <div className="relative w-full rounded-[26px] border border-white/10 bg-gradient-to-br from-white/[0.16] via-white/[0.07] to-white/[0.03] p-2 shadow-[0_30px_70px_-16px_rgba(2,8,24,0.75)] sm:p-2.5">
      {/* dashoffset is 2× the 12-unit dash period, so the loop restarts without a jump. */}
      <style>{`@keyframes df-edge-flow { to { stroke-dashoffset: -24; } }
.df-edge { stroke-dasharray: 4 8; animation: df-edge-flow 2.4s linear infinite; }`}</style>

      <div className="overflow-hidden rounded-[20px] border border-slate-200/70 bg-gradient-to-b from-white via-[#f7f9fd] to-[#e9eff8] p-4 sm:p-6">
        {/* One grid for captions and content, so all three columns share one template.
            DOM order is label→content→label→content→label→content, which is exactly the
            reading order when the grid collapses to one column below `md`. At `md` and up
            every item is placed explicitly, because auto-placement would otherwise put
            the second label beside the first column's chips. */}
        {/* Track widths are fixed px, not `max-content`/%: the hub is HUB_PCT of the
            middle track, so that track needs a floor (210 px → a ≥130 px hub, which is
            what "Bestellungen" needs) and a ceiling (or the hub stretches when the card
            is wide). `justify-center` centres the tracks in the leftover space. */}
        <div className="grid grid-cols-1 gap-y-6 md:grid-cols-[104px_minmax(210px,340px)_104px] md:grid-rows-[auto_340px] md:justify-center md:gap-y-0">
          <ColumnLabel align="left" place="md:col-start-1 md:row-start-1">
            {f.sourcesLabel}
          </ColumnLabel>
          <div className="flex flex-col justify-between gap-2 md:col-start-1 md:row-start-2 md:gap-0">
            {SOURCE_SYSTEMS.map((name) => (
              <Chip key={name} label={name} />
            ))}
          </div>

          <ColumnLabel align="center" place="md:col-start-2 md:row-start-1">
            {f.hubTitle}
          </ColumnLabel>
          {/* Middle column: the edges span exactly this cell, so their x coordinates are
              a function of HUB_PCT alone. */}
          <div className="relative flex items-center justify-center md:col-start-2 md:row-start-2">
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full md:block"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="dfEdgeGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(14,165,233,0.28)" />
                  <stop offset="50%" stopColor="rgba(2,132,199,0.95)" />
                  <stop offset="100%" stopColor="rgba(14,165,233,0.28)" />
                </linearGradient>
              </defs>
              {EDGE_PATHS.map((d) => (
                <g key={d}>
                  {/* Two paths per edge: the solid slate line carries the legibility,
                      the dashed sky overlay carries the motion. A dashed line alone is
                      what read as "barely visible" — on a light surface it is a row of
                      specks, not a connection. Only the animated one is `.df-edge`. */}
                  <path
                    d={d}
                    fill="none"
                    stroke="rgba(100,116,139,0.42)"
                    strokeWidth="1.25"
                    vectorEffect="non-scaling-stroke"
                  />
                  <path
                    className="df-edge"
                    d={d}
                    fill="none"
                    stroke="url(#dfEdgeGrad)"
                    strokeWidth="1.25"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              ))}
            </svg>

            <div
              className="relative z-10 w-full rounded-2xl border border-slate-900/10 bg-gradient-to-b from-[#12294a] to-[#0a1a30] p-4 text-center shadow-[0_14px_34px_-10px_rgba(9,32,64,0.6)]"
              // minWidth is the safety valve: if the track ever falls below its floor the
              // hub covers a few px of the edge tips (it sits above them) instead of
              // clipping its own labels, which is what happened at 1024 px before.
              style={{ maxWidth: `${HUB_PCT}%`, minWidth: '132px' }}
            >
              <ul className="space-y-1.5">
                {f.hubItems.map((item) => (
                  <li
                    key={item}
                    className="rounded-md bg-white/[0.09] px-2 py-1 text-[0.78rem] font-medium text-sky-50"
                  >
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[0.68rem] leading-snug text-slate-300/80">{f.hubNote}</p>
            </div>
          </div>

          <ColumnLabel align="right" place="md:col-start-3 md:row-start-1">
            {f.targetsLabel}
          </ColumnLabel>
          <div className="flex flex-col justify-between gap-2 md:col-start-3 md:row-start-2 md:gap-0">
            {TARGET_SYSTEMS.map((name) => (
              <Chip key={name} label={name} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const { lang, t } = useTranslation();

  // German slugs under /en resolve via EN_ALIAS_REDIRECTS, same as the Navbar.
  const withLang = (to: string) => `${lang === 'en' ? '/en' : '/de'}${to}`;

  return (
    <section
      id="hero"
      data-nav-theme="dark"
      className="relative isolate min-h-[100svh] overflow-x-clip pt-24 text-slate-100 sm:pt-32"
    >
      <MeshBackground mode="container" />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(circle at 18% 12%, rgba(25, 72, 170, 0.12), transparent 54%), radial-gradient(circle at 82% 88%, rgba(30, 112, 210, 0.1), transparent 60%), linear-gradient(180deg, rgba(5, 9, 20, 0.16) 0%, rgba(3, 8, 20, 0.24) 100%)',
        }}
      />

      {/* The two-column split starts at `xl`, not `lg`: at 1024 px the graph column is
          only ~380 px wide, which is below the graph's own minimum (2×104 chip + 210 hub
          track) and used to squeeze the hub until "Bestellungen" clipped. Below `xl` the
          text sits above a full-width graph, where everything has room. */}
      <div className="relative z-10 mx-auto grid w-full max-w-[1320px] grid-cols-1 gap-8 px-4 pb-10 sm:px-6 sm:pb-16 lg:gap-10 lg:px-8 lg:pb-12 xl:min-h-[calc(100svh-132px)] xl:grid-cols-[1.12fr_0.88fr] xl:items-center">
        <div className="max-w-[680px]">
          <div className="hero-animate mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-300/20 bg-slate-900/40 px-3 py-1.5 font-mono text-xs text-sky-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.9)]" />
              <span>{t.hero.badge}</span>
            </div>
          </div>

          {/* The headline is long. It is sized to read as two lines plus the highlighted
              third, not as six cramped ones — hence the wider column and the step-down
              type scale rather than the old 3.35rem. */}
          <div className="hero-animate mb-6" style={{ animationDelay: '120ms' }}>
            <h1
              className="font-display font-semibold leading-[1.14] tracking-tight-premium"
              style={{ fontSize: 'clamp(1.7rem, 3.1vw, 2.9rem)' }}
            >
              <span className="text-slate-100">{t.hero.headlineLine1}</span>{' '}
              <span className="block bg-gradient-to-r from-sky-400 via-cyan-300 to-blue-200 bg-clip-text text-transparent">
                {t.hero.headlineLine2}
              </span>
            </h1>
          </div>

          <p
            className="hero-animate mb-7 max-w-[52ch] text-[1.02rem] leading-relaxed text-slate-300/92"
            style={{ animationDelay: '260ms' }}
          >
            {t.hero.subheadline}
          </p>

          {/* Side by side. `flex-wrap` is the phone-width safety valve, not the layout. */}
          <div
            className="hero-animate flex flex-wrap items-center gap-3"
            style={{ animationDelay: '340ms' }}
          >
            <Link
              to={withLang('/integrationen')}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-2xl border border-transparent bg-sky-100 px-5 py-3 text-base font-medium text-slate-900 transition-all duration-300 hover:bg-white"
            >
              {t.hero.ctaPrimary}
            </Link>
            <Link
              to={withLang('/pricing')}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-2xl border border-sky-300/25 px-5 py-3 text-base font-medium text-slate-100 transition-all duration-300 hover:border-sky-300/50 hover:bg-white/[0.06]"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>

        <div
          className="hero-animate w-full xl:justify-self-end"
          style={{ animationDelay: '220ms' }}
        >
          <IntegrationFlowGraph />
        </div>
      </div>
    </section>
  );
}
